import { useQuery } from '@tanstack/react-query';
import { apiClient, publicApiClient } from 'api';

/**
 * Which assessment metrics the recruiter kept in the hiring flow. A metric the
 * recruiter deleted comes back as `false` and must be hidden from the report
 * (and public report). Keys mirror the backend `/assessment/sections/` payload.
 */
export interface AssessmentSectionsMap {
  basic_expectations?: boolean;
  soft_skills?: boolean;
  work_culture?: boolean;
  workplace_quality?: boolean;
  work_style?: boolean;
  personality?: boolean;
  domain_knowledge?: boolean;
}

interface AssessmentSectionsResponse {
  message: string;
  data: {
    assessment_id: string;
    sections: AssessmentSectionsMap;
  };
}

/**
 * Maps a report tab label to the `/assessment/sections/` flag that controls it.
 * Tabs with no entry here (e.g. 'Summary') are structural and always shown.
 */
export const REPORT_TAB_SECTION_KEY: Record<string, keyof AssessmentSectionsMap> = {
  'Basic Expectation': 'basic_expectations',
  'Soft Skills': 'soft_skills',
  'Work Style': 'work_style',
  'Workplace Qualities': 'workplace_quality',
  'Work Motivators': 'work_culture',
  Personality: 'personality',
  'Domain Knowledge': 'domain_knowledge',
};

/**
 * A section is shown unless the endpoint explicitly reports it as `false`.
 * When the map is absent (still loading, or the request failed) we default to
 * showing everything so a fetch hiccup never hides legitimate sections.
 */
export const isSectionEnabled = (
  sections: AssessmentSectionsMap | undefined,
  key: keyof AssessmentSectionsMap,
): boolean => {
  if (!sections) return true;
  return sections[key] !== false;
};

/** Same rule as {@link isSectionEnabled}, but keyed by a report tab label. */
export const isTabEnabled = (
  sections: AssessmentSectionsMap | undefined,
  tab: string,
): boolean => {
  const key = REPORT_TAB_SECTION_KEY[tab];
  if (!key) return true; // Structural tab (Summary) — never gated by sections.
  return isSectionEnabled(sections, key);
};

/**
 * Candidate-facing name for each assessment section, in the order the candidate
 * meets them. Labels mirror the section labels used in the assessment itself
 * (see `features/assessment`), so the pre-assessment preview and the assessment
 * agree. `basic_expectations` is deliberately absent — it is the profile form,
 * not a question area.
 */
export const CANDIDATE_SECTION_AREAS: {
  key: keyof AssessmentSectionsMap;
  label: string;
}[] = [
  { key: 'domain_knowledge', label: 'Professional Expertise' },
  { key: 'soft_skills', label: 'Interpersonal Effectiveness' },
  { key: 'work_culture', label: 'Work Drivers' },
  { key: 'workplace_quality', label: 'Work Environment Preferences' },
  { key: 'work_style', label: 'Execution Approach' },
  { key: 'personality', label: 'Personality' },
];

/**
 * The areas a candidate will actually be assessed on, derived from the sections
 * the recruiter kept. Returns undefined while the map is unresolved (loading or
 * failed) so the caller can keep showing its own fallback list.
 */
export const getCandidateAreasCovered = (
  sections: AssessmentSectionsMap | undefined,
): string[] | undefined => {
  if (!sections) return undefined;
  return CANDIDATE_SECTION_AREAS
    .filter(({ key }) => isSectionEnabled(sections, key))
    .map(({ label }) => label);
};

/**
 * Best-effort match of an alignment-overview row's freeform `category` label to
 * a section key, so a deleted metric's row can be dropped from the summary too.
 * Returns undefined when nothing matches confidently (row is then kept).
 */
export const matchCategoryToSectionKey = (
  category: string | undefined,
): keyof AssessmentSectionsMap | undefined => {
  const c = (category || '').toLowerCase();
  if (!c) return undefined;
  if (c.includes('basic')) return 'basic_expectations';
  if (c.includes('soft skill')) return 'soft_skills';
  if (c.includes('work style')) return 'work_style';
  if (c.includes('motivat')) return 'work_culture';
  if (c.includes('qualit')) return 'workplace_quality';
  if (c.includes('personalit')) return 'personality';
  if (c.includes('domain')) return 'domain_knowledge';
  return undefined;
};

/**
 * Hook for fetching which sections a recruiter kept for an assessment. Used to
 * hide report sections/tabs for metrics that were deleted in the hiring flow,
 * and to list the areas a candidate will be assessed on.
 *
 * Pass `anonymous` on candidate-facing pages: it bypasses the shared instance's
 * interceptors so a 401 can't clear storage and bounce the candidate to sign-in.
 */
export const useAssessmentSections = (
  assessmentId?: string | null,
  { anonymous = false }: { anonymous?: boolean } = {},
) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['assessmentSections', assessmentId, anonymous],
    queryFn: async () => {
      const params = { assessment_id: assessmentId };
      const client = anonymous ? publicApiClient : apiClient;
      const response = await client.get<AssessmentSectionsResponse>(
        '/assessment/sections/',
        { params },
      );
      return response.data.data;
    },
    enabled: !!assessmentId,
    staleTime: 5 * 60 * 1000,
    retry: 1,
    refetchOnWindowFocus: false,
  });

  return {
    sections: data?.sections,
    isLoading,
    error,
  };
};
